<script setup lang="ts">
import {NButton, NTooltip} from 'naive-ui';
import type {Component, PropType} from "vue";
import {computed} from "vue";
import {renderIcon} from "@/helpers/render-helpers";

const emit = defineEmits(['click']);
const props = defineProps({
  text: {
    type: String,
    default: "",
    required: false,
  },
  icon: {
    type: Object as PropType<Component>,
    required: false,
  },
  iconProps: {
    type: Object,
    required: false,
  },
  tooltip: {
    type: String,
    required: false,
    default: "",
  },
  // Verplicht zodra de knop alleen een icoon toont: zonder tekst heeft hij anders geen toegankelijke naam,
  // en een tooltip levert er geen (die beschrijft, hij benoemt niet). Laat leeg bij een knop mét tekst —
  // die spreekt voor zich en zou zich anders dubbel aankondigen.
  ariaLabel: {
    type: String,
    required: false,
  },
  type: {
    type: String as PropType<"default" | "tertiary" | "primary" | "info" | "success" | "warning" | "error">,
    default: "primary"
  },
  buttonStyle: {
    type: String as PropType<"primary" | "secondary" | "tertiary" | "quaternary">,
    default: "primary"
  },
  shape: {
    type: String as PropType<"rect" | "round" | "circle">,
    default: "rect",
  },
  size: {
    type: String as PropType<"small" | "tiny" | "medium" | "large">,
    default: "medium"
  },
  textOnly: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  tag: {
    type: String as PropType<keyof HTMLElementTagNameMap>,
    default: "button",
    required: false,
  },
  href: {
    type: String,
    required: false,
  },
  ghost: {
    type: Boolean,
    default: false
  },
  focusable: {
    type: Boolean,
    default: true
  },
  bordered: {
    type: Boolean,
    default: true
  },
  // Note: Setting the color of an icon might make it invisible in light/dark mode. Make sure to test both light and dark mode
  color: {
    type: String,
    required: false
  },
  fullWidth: {
    type: Boolean,
    required: false,
    default: false
  },
  htmlClasses: {
    type: [String, Array, Object],
    default: () => '',
  },
})

const iconRenderFunc = computed(() => props.icon ? renderIcon(props.icon, props.iconProps) : undefined);

const handleClick = (event: MouseEvent) => {
  emit("click", event);
}

</script>

<template>
  <n-tooltip v-if="tooltip">
    <template #trigger>
      <n-button :size="size"
                :disabled="disabled"
                :type="type"
                :tag="tag"
                :href="href"
                :loading="loading"
                :text="textOnly"
                :aria-label="ariaLabel"
                :render-icon="iconRenderFunc"
                :rect="shape === 'rect'"
                :round="shape === 'round'"
                :circle="shape === 'circle'"
                :secondary="buttonStyle === 'secondary'"
                :tertiary="buttonStyle === 'tertiary'"
                :quaternary="buttonStyle === 'quaternary'"
                :ghost="ghost"
                :focusable="focusable"
                :color="color"
                :bordered="bordered"
                @click="handleClick"
                :class="fullWidth ? `full-width ${htmlClasses}` : htmlClasses">
        <span v-if="text">
          {{ text }}
        </span>
      </n-button>
    </template>
    {{ tooltip }}
  </n-tooltip>
  <n-button v-else
            :size="size"
            :disabled="disabled"
            :type="type"
            :tag="tag"
            :href="href"
            :loading="loading"
            :text="textOnly"
            :aria-label="ariaLabel"
            :render-icon="iconRenderFunc"
            :rect="shape === 'rect'"
            :round="shape === 'round'"
            :circle="shape === 'circle'"
            :secondary="buttonStyle === 'secondary'"
            :tertiary="buttonStyle === 'tertiary'"
            :quaternary="buttonStyle === 'quaternary'"
            :ghost="ghost"
            :focusable="focusable"
            :color="color"
            :bordered="bordered"
            @click="handleClick"
            :class="fullWidth ? `full-width ${htmlClasses}` : htmlClasses">
    <span v-if="text">
      {{ text }}
    </span>
  </n-button>
</template>

<style scoped lang="scss">
</style>
